/* ============================================================================
   Snowcorp Avenue — theme.js
   Core interactivity as a dependency-free ES module. Everything is built on
   native Custom Elements + a tiny pub/sub bus so sections stay decoupled.

   Modules:
     • utils            fetch/debounce/focus-trap/money helpers
     • PubSub           cross-component events (cart:updated, drawer:open…)
     • Reveal           IntersectionObserver scroll animations
     • StickyHeader     transparent-on-hero + shrink-on-scroll
     • CartAPI          AJAX add / change / update
     • <cart-drawer>    slide-in cart with free-shipping progress
     • <product-form>   AJAX add-to-cart with state
     • <quantity-input> stepper
     • <predictive-search>
     • Wishlist         localStorage store + <wishlist-button>
     • RecentlyViewed   localStorage tracker
     • ThemeToggle      dark / light persistence
   ========================================================================== */

const Snow = window.SnowcorpTheme || {};

/* ── utils ─────────────────────────────────────────────────────────────── */
const utils = {
  debounce(fn, wait = 300) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  },

  async fetchJSON(url, options = {}) {
    const res = await fetch(url, {
      ...options,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(options.headers || {}) },
    });
    const data = await res.json();
    if (!res.ok) throw Object.assign(new Error(data.description || 'Request failed'), { data });
    return data;
  },

  async fetchText(url) {
    const res = await fetch(url);
    return res.text();
  },

  formatMoney(cents) {
    const value = (cents / 100).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return (Snow.moneyFormat || '${{amount}}').replace(/\{\{\s*amount[^}]*\}\}/, value);
  },

  trapFocus(container, initial) {
    const focusables = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    (initial || first)?.focus();

    function handle(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    container.addEventListener('keydown', handle);
    return () => container.removeEventListener('keydown', handle);
  },

  lockScroll(lock = true) {
    document.body.classList.toggle('is-locked', lock);
  },
};

/* ── PubSub bus ────────────────────────────────────────────────────────── */
const bus = new EventTarget();
const PubSub = {
  emit: (name, detail) => bus.dispatchEvent(new CustomEvent(name, { detail })),
  on: (name, cb) => bus.addEventListener(name, cb),
  off: (name, cb) => bus.removeEventListener(name, cb),
};
const EVENTS = {
  cartUpdated: 'cart:updated',
  cartError: 'cart:error',
  drawerOpen: 'drawer:open',
};

/* ── Reveal on scroll ──────────────────────────────────────────────────── */
class Reveal {
  constructor() {
    if (!Snow.motion?.enabled || !('IntersectionObserver' in window)) {
      document.querySelectorAll('[data-reveal], [data-reveal-stagger]').forEach((el) =>
        el.classList.add('is-visible')
      );
      return;
    }
    this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.1,
    });
    this.observe();
    // Re-scan after theme-editor section loads
    document.addEventListener('shopify:section:load', () => this.observe());
  }

  observe() {
    document
      .querySelectorAll('[data-reveal]:not(.is-visible), [data-reveal-stagger]:not(.is-visible)')
      .forEach((el) => this.observer.observe(el));
  }

  onIntersect(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        this.observer.unobserve(entry.target);
      }
    });
  }
}

/* ── Lazy image fade-in ────────────────────────────────────────────────── */
function initLazyImages() {
  document.querySelectorAll('img[loading="lazy"]').forEach((img) => {
    if (img.complete) img.classList.add('is-loaded');
    else img.addEventListener('load', () => img.classList.add('is-loaded'), { once: true });
  });
}

/* ── Parallax ──────────────────────────────────────────────────────────────
   Shared handler for any [data-parallax] element (editorial banner, image-with-
   text, etc.). The attribute value is the intensity (0–1). Only elements in view
   are transformed, on a single rAF-throttled scroll listener. Fully gated by the
   motion setting and prefers-reduced-motion — a no-op otherwise, so sections that
   opt in simply render a static, slightly over-scaled image. */
class Parallax {
  constructor() {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!Snow.motion?.enabled || !Snow.motion?.parallax || reduced || !('IntersectionObserver' in window)) return;

    this.items = [];
    this.ticking = false;
    this.io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        const item = this.items.find((i) => i.el === e.target);
        if (item) item.visible = e.isIntersecting;
      });
      this.request();
    }, { rootMargin: '10% 0px' });

    this.collect();
    window.addEventListener('scroll', () => this.request(), { passive: true });
    window.addEventListener('resize', () => this.request(), { passive: true });
    document.addEventListener('shopify:section:load', () => this.collect());
    this.request();
  }

  collect() {
    document.querySelectorAll('[data-parallax]:not([data-parallax-bound])').forEach((el) => {
      el.setAttribute('data-parallax-bound', '');
      el.style.willChange = 'transform';
      const item = { el, speed: parseFloat(el.dataset.parallax) || 0.12, visible: false };
      this.items.push(item);
      this.io.observe(el);
    });
  }

  request() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => this.update());
  }

  update() {
    this.ticking = false;
    const vh = window.innerHeight;
    this.items.forEach((item) => {
      if (!item.visible) return;
      const rect = item.el.getBoundingClientRect();
      // -1 (below) → 1 (above) relative to viewport centre
      const progress = (rect.top + rect.height / 2 - vh / 2) / (vh / 2 + rect.height / 2);
      const shift = -progress * item.speed * 100;
      item.el.style.transform = `translate3d(0, ${shift.toFixed(2)}px, 0)`;
    });
  }
}

/* ── Sticky header ─────────────────────────────────────────────────────── */
class StickyHeader {
  constructor() {
    this.header = document.querySelector('[data-header]');
    if (!this.header) return;
    this.lastY = 0;
    this.threshold = 40;
    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();
  }

  onScroll() {
    const y = window.scrollY;
    this.header.classList.toggle('is-scrolled', y > this.threshold);
    // Hide on scroll-down, reveal on scroll-up (only once past hero)
    if (y > 300) {
      this.header.classList.toggle('is-hidden', y > this.lastY && !this.header.matches(':hover'));
    } else {
      this.header.classList.remove('is-hidden');
    }
    this.lastY = y;
  }
}

/* ── Cart API ──────────────────────────────────────────────────────────── */
const CartAPI = {
  async add(formData) {
    const data = await utils.fetchJSON(`${Snow.routes.cartAdd}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(formData),
    });
    await this.refresh();
    return data;
  },

  async change(key, quantity) {
    const cart = await utils.fetchJSON(`${Snow.routes.cartChange}.js`, {
      method: 'POST',
      body: JSON.stringify({ id: key, quantity }),
    });
    PubSub.emit(EVENTS.cartUpdated, { cart });
    return cart;
  },

  async updateNote(note) {
    return utils.fetchJSON(`${Snow.routes.cartUpdate}.js`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    });
  },

  async get() {
    return utils.fetchJSON(`${Snow.routes.cart}.js`);
  },

  async refresh() {
    const cart = await this.get();
    PubSub.emit(EVENTS.cartUpdated, { cart });
    return cart;
  },
};

/* ── <product-form> — AJAX add to cart ─────────────────────────────────── */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.button = this.querySelector('[type="submit"]');
    this.form?.addEventListener('submit', this.onSubmit.bind(this));
  }

  async onSubmit(e) {
    e.preventDefault();
    if (this.button.getAttribute('aria-disabled') === 'true') return;

    this.setLoading(true);
    try {
      const formData = new FormData(this.form);
      await CartAPI.add(formData);
      PubSub.emit(EVENTS.drawerOpen, { source: 'add' });
      this.flashSuccess();
    } catch (err) {
      PubSub.emit(EVENTS.cartError, { message: err.data?.description || Snow.strings.cartError });
      this.flashError(err.data?.description);
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(loading) {
    this.button.classList.toggle('is-loading', loading);
    this.button.setAttribute('aria-busy', loading);
  }

  flashSuccess() {
    const label = this.button.querySelector('[data-add-label]') || this.button;
    const original = label.textContent;
    label.textContent = '✓';
    setTimeout(() => (label.textContent = original), 1200);
  }

  flashError(msg) {
    const errEl = this.querySelector('[data-form-error]');
    if (errEl && msg) {
      errEl.textContent = msg;
      errEl.hidden = false;
    }
  }
}

/* ── <quantity-input> ──────────────────────────────────────────────────── */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.querySelectorAll('button').forEach((btn) =>
      btn.addEventListener('click', () => this.step(btn.dataset.step === 'up' ? 1 : -1))
    );
  }

  step(direction) {
    const min = parseInt(this.input.min || '1', 10);
    const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
    let value = parseInt(this.input.value || '1', 10) + direction;
    value = Math.min(max, Math.max(min, value));
    this.input.value = value;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

/* ── <cart-drawer> ─────────────────────────────────────────────────────── */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.scrim = document.querySelector('[data-overlay-scrim]');
    this.querySelectorAll('[data-drawer-close]').forEach((b) =>
      b.addEventListener('click', () => this.close())
    );
    this.scrim?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => e.key === 'Escape' && this.close());

    PubSub.on(EVENTS.drawerOpen, () => this.open());
    PubSub.on(EVENTS.cartUpdated, (e) => this.render(e.detail.cart));

    // Bind toggles anywhere in the DOM (header cart icon, etc.)
    document.querySelectorAll('[data-cart-toggle]').forEach((el) =>
      el.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      })
    );

    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('change', this.onChange.bind(this));
  }

  open() {
    if (Snow.cart?.type !== 'drawer') return;
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    this.scrim?.classList.add('is-visible');
    utils.lockScroll(true);
    this.releaseFocus = utils.trapFocus(this, this.querySelector('[data-drawer-close]'));
  }

  close() {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    this.scrim?.classList.remove('is-visible');
    utils.lockScroll(false);
    this.releaseFocus?.();
  }

  onClick(e) {
    const remove = e.target.closest('[data-cart-remove]');
    if (remove) {
      e.preventDefault();
      CartAPI.change(remove.dataset.key, 0);
    }
  }

  onChange(e) {
    const qty = e.target.closest('[data-cart-qty]');
    if (qty) CartAPI.change(qty.dataset.key, parseInt(qty.value, 10));
  }

  async render(cart) {
    // Pull a fresh drawer section render and swap its inner list + footer
    try {
      const html = await utils.fetchText(`${Snow.routes.root}?section_id=cart-drawer`);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      ['[data-drawer-items]', '[data-drawer-footer]', '[data-free-shipping]'].forEach((sel) => {
        const next = doc.querySelector(sel);
        const current = this.querySelector(sel);
        if (next && current) current.replaceWith(next);
      });
    } catch (_) {
      /* non-fatal: count badge still updates below */
    }
    this.updateCount(cart.item_count);
  }

  updateCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }
}

/* ── <predictive-search> ───────────────────────────────────────────────── */
class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    if (!Snow.features?.predictiveSearch) return;
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-search-results]');
    this.input?.addEventListener('input', utils.debounce(this.onInput.bind(this), 280));
    this.input?.addEventListener('focus', () => this.query && this.open());
    document.addEventListener('click', (e) => !this.contains(e.target) && this.close());
  }

  async onInput() {
    this.query = this.input.value.trim();
    if (this.query.length < 2) return this.close();
    const params = new URLSearchParams({
      q: this.query,
      'resources[type]': 'product,collection,page,article',
      'resources[limit]': 6,
      section_id: 'predictive-search',
    });
    try {
      const html = await utils.fetchText(`${Snow.routes.predictiveSearch}?${params}`);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('[data-search-results]');
      if (next) this.results.innerHTML = next.innerHTML;
      this.open();
    } catch (_) {
      this.close();
    }
  }

  open() {
    this.classList.add('is-open');
    this.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.classList.remove('is-open');
    this.setAttribute('aria-expanded', 'false');
  }
}

/* ── Wishlist (localStorage) ───────────────────────────────────────────── */
const Wishlist = {
  key: 'snowcorp-wishlist',
  get() {
    try {
      return JSON.parse(localStorage.getItem(this.key)) || [];
    } catch {
      return [];
    }
  },
  has(handle) {
    return this.get().includes(handle);
  },
  toggle(handle) {
    const list = this.get();
    const idx = list.indexOf(handle);
    if (idx > -1) list.splice(idx, 1);
    else list.push(handle);
    localStorage.setItem(this.key, JSON.stringify(list));
    PubSub.emit('wishlist:updated', { list });
    this.updateBadge();
    return idx === -1;
  },
  updateBadge() {
    const count = this.get().length;
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  },
};

class WishlistButton extends HTMLElement {
  connectedCallback() {
    if (!Snow.features?.wishlist) return (this.hidden = true);
    this.handle = this.dataset.handle;
    this.button = this.querySelector('button') || this;
    this.sync();
    this.button.addEventListener('click', (e) => {
      e.preventDefault();
      const added = Wishlist.toggle(this.handle);
      this.sync(added);
    });
    PubSub.on('wishlist:updated', () => this.sync());
  }
  sync(state) {
    const active = state ?? Wishlist.has(this.handle);
    this.button.classList.toggle('is-active', active);
    this.button.setAttribute('aria-pressed', active);
  }
}

/* ── Recently viewed ───────────────────────────────────────────────────── */
const RecentlyViewed = {
  key: 'snowcorp-recently-viewed',
  track(handle) {
    if (!handle || !Snow.features?.recentlyViewed) return;
    let list = this.get().filter((h) => h !== handle);
    list.unshift(handle);
    localStorage.setItem(this.key, JSON.stringify(list.slice(0, 12)));
  },
  get() {
    try {
      return JSON.parse(localStorage.getItem(this.key)) || [];
    } catch {
      return [];
    }
  },
};

/* ── Theme (dark/light) toggle ─────────────────────────────────────────── */
class ThemeToggle extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', () => {
      const next = document.documentElement.getAttribute('data-theme') === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', next);
      try {
        localStorage.setItem('snowcorp-theme', next);
      } catch {}
      this.setAttribute('aria-pressed', next === 'dark');
    });
  }
}

/* ── Cart error toast ──────────────────────────────────────────────────── */
PubSub.on(EVENTS.cartError, (e) => {
  let toast = document.querySelector('[data-toast]');
  if (!toast) {
    toast = document.createElement('div');
    toast.setAttribute('data-toast', '');
    toast.className = 'toast';
    document.body.appendChild(toast);
  }
  toast.textContent = e.detail.message;
  toast.classList.add('is-visible');
  clearTimeout(this._toastTimer);
  this._toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 4000);
});

/* ── Boot ──────────────────────────────────────────────────────────────── */
function defineElements() {
  const registry = [
    ['product-form', ProductForm],
    ['quantity-input', QuantityInput],
    ['cart-drawer', CartDrawer],
    ['predictive-search', PredictiveSearch],
    ['wishlist-button', WishlistButton],
    ['theme-toggle', ThemeToggle],
  ];
  registry.forEach(([tag, cls]) => {
    if (!customElements.get(tag)) customElements.define(tag, cls);
  });
}

function init() {
  defineElements();
  new Reveal();
  new StickyHeader();
  new Parallax();
  initLazyImages();
  Wishlist.updateBadge();

  if (Snow.motion?.smoothScroll) document.documentElement.classList.add('smooth-scroll');

  // Track PDP views for "recently viewed"
  const pdp = document.querySelector('[data-product-handle]');
  if (pdp) RecentlyViewed.track(pdp.dataset.productHandle);

  // Refresh dynamic bits after theme-editor edits
  document.addEventListener('shopify:section:load', initLazyImages);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Expose a minimal API for section scripts / debugging
window.Snowcorp = { CartAPI, Wishlist, RecentlyViewed, PubSub, EVENTS, utils };
